'use client';

import * as React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Check, CalendarPlus, Plus, ArrowRight, Video, Link2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { CopyLinkButton, InlineCopyField } from '@/shared/components/CopyLinkButton';
import { MobileActionBar } from '@/shared/components/MobileActionBar';
import { GENTLE } from '@/lib/motion';
import { meetingStamp } from '@/lib/format';
import { downloadICS } from '@/lib/ics';
import { currentOrigin } from '@/features/meetings';
import { type CreatedRoom } from '@/features/rooms';

/**
 * Post-creation "meeting ready" moment (§4.1). One clear job: get the guest link
 * out. So the link sits in a modern copy-field (icon in the field, no big
 * button), the single primary CTA takes the host in, and everything else — the
 * host's own link, calendar, create-another — is a quiet row of small actions.
 * The host link is never the guest's; it must never be sent to them.
 */
export function CreatedResult({
  created,
  onCreateAnother,
}: {
  created: CreatedRoom;
  onCreateAnother: () => void;
}) {
  const { hostPath, guestName, scheduledStart, meetingType, joinUrl } = created;
  const isInstant = meetingType === 'instant';
  const primaryLabel = isInstant ? 'Continue to meeting' : 'Join as host';

  // roomsApi.create() already returns the guest link fully formed (signed
  // ?t= token baked in) — no client-side reconstruction needed, unlike the
  // Meeting model's guestLink(). The host link IS client-side nav, so it's
  // just the relative path off the current origin.
  const guestUrl = joinUrl;
  const hostUrl = `${currentOrigin()}${hostPath}`;

  const addToCalendar = React.useCallback(() => {
    if (!scheduledStart) return;
    downloadICS(`${guestName}.ics`, {
      uid: `${created.roomId}@nsi-meet`,
      title: `Meeting with ${guestName}`,
      start: new Date(scheduledStart),
      description: `Join: ${guestUrl}`,
      url: guestUrl,
    });
  }, [scheduledStart, guestName, guestUrl, created.roomId]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={GENTLE}
      className="mx-auto w-full max-w-md space-y-6 max-sm:pb-24"
    >
      {/* Header */}
      <div className="flex flex-col items-center gap-3 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--success-soft)]">
          <Check className="h-6 w-6 text-[var(--success)]" aria-hidden="true" />
        </span>
        <div className="space-y-1">
          <h2 className="font-heading text-lg font-semibold tracking-tight text-[var(--text-primary)]">
            {isInstant ? 'Your meeting is live' : 'Meeting scheduled'}
          </h2>
          <p className="text-sm text-[var(--text-muted)]">
            {isInstant
              ? 'Share the link with your guest, then hop in.'
              : scheduledStart
                ? meetingStamp(new Date(scheduledStart))
                : 'Share the link with your guest.'}
          </p>
        </div>
      </div>

      {/* Guest link — the one thing that matters, in a copy-field */}
      <div className="space-y-1.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
          Guest link
        </p>
        <InlineCopyField
          url={guestUrl}
          label="Copy guest link"
          successMessage="Guest link copied"
        />
        <p className="text-xs text-[var(--text-muted)]">
          Anyone with this link joins as your guest.
        </p>
      </div>

      {/* Single primary action — client-side nav so the room's branded loader
          shows during the gate resolve instead of a full-reload white flash.
          Desktop: inline. Phone: pinned to the bottom thumb zone. */}
      <Button asChild size="lg" className="hidden w-full sm:flex">
        <Link href={hostPath}>
          {isInstant ? <Video className="h-4.5 w-4.5" aria-hidden="true" /> : null}
          {primaryLabel}
          {isInstant ? null : <ArrowRight className="h-4.5 w-4.5" aria-hidden="true" />}
        </Link>
      </Button>

      <MobileActionBar>
        <Button asChild size="lg" className="w-full">
          <Link href={hostPath}>
            {isInstant ? <Video className="h-4.5 w-4.5" aria-hidden="true" /> : null}
            {primaryLabel}
            {isInstant ? null : <ArrowRight className="h-4.5 w-4.5" aria-hidden="true" />}
          </Link>
        </Button>
      </MobileActionBar>

      {/* Quiet secondary row */}
      <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1 border-t border-[var(--border)] pt-4">
        <CopyLinkButton
          url={hostUrl}
          idleLabel="Host link"
          copiedLabel="Copied"
          successMessage="Host link copied"
          variant="ghost"
          size="sm"
          icon={<Link2 className="h-4 w-4" aria-hidden="true" />}
        />
        {!isInstant && scheduledStart && (
          <Button variant="ghost" size="sm" onClick={addToCalendar}>
            <CalendarPlus className="h-4 w-4" aria-hidden="true" />
            Calendar
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onCreateAnother}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          New meeting
        </Button>
      </div>
    </motion.div>
  );
}
